import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { MoreHorizontalIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useRevokeSession } from "@/hooks/auth/use-revoke-session";
import { authClient, useSession } from "@/lib/auth-client";
import { noIndexSeo } from "@/lib/seo";
import * as m from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";

export const Route = createFileRoute("/(app)/settings/account")({
	component: RouteComponent,
	head: ({ match }) =>
		noIndexSeo({
			title: `${m.accountSettings_title()} | StudyOS`,
			description: m.accountSettings_description(),
			pathname: match.pathname,
		}),
});

function RouteComponent() {
	return (
		<div className="space-y-6">
			<div>
				<h3 className="text-lg font-medium">{m.accountSettings_title()}</h3>
				<p className="text-sm text-muted-foreground">{m.accountSettings_description()}</p>
			</div>
			<Separator />
			<ActiveSessions />
		</div>
	);
}

function ActiveSessions() {
	const { data: currentSession } = useSession();

	const { data: sessions, isPending } = useQuery({
		queryKey: ["sessions"],
		queryFn: async () => {
			const result = await authClient.listSessions();

			return result.data;
		},
	});

	const { mutate: revokeSession } = useRevokeSession();

	const formatDate = (date: Date | string) => {
		const d = new Date(date);
		const locale = getLocale();

		return d.toLocaleString(locale, {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		});
	};

	if (isPending) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="text-base">
						{m.accountSettings_activeSessions()}
					</CardTitle>
				</CardHeader>
				<CardContent>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>{m.accountSettings_device()}</TableHead>
								<TableHead>{m.accountSettings_location()}</TableHead>
								<TableHead>{m.accountSettings_createdAt()}</TableHead>
								<TableHead>{m.accountSettings_updatedAt()}</TableHead>
								<TableHead className="w-10" />
							</TableRow>
						</TableHeader>
						<TableBody>
							{Array.from({ length: 3 }).map((_, i) => (
								<TableRow key={i}>
									<TableCell>
										<Skeleton className="h-4 w-48" />
									</TableCell>
									<TableCell>
										<Skeleton className="h-4 w-24" />
									</TableCell>
									<TableCell>
										<Skeleton className="h-4 w-32" />
									</TableCell>
									<TableCell>
										<Skeleton className="h-4 w-32" />
									</TableCell>
									<TableCell>
										<Skeleton className="h-8 w-8" />
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-base">{m.accountSettings_activeSessions()}</CardTitle>
			</CardHeader>
			<CardContent>
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>{m.accountSettings_device()}</TableHead>
							<TableHead>{m.accountSettings_location()}</TableHead>
							<TableHead>{m.accountSettings_createdAt()}</TableHead>
							<TableHead>{m.accountSettings_updatedAt()}</TableHead>
							<TableHead className="w-10" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{sessions?.map((session) => {
							const isCurrentSession = currentSession?.session?.id === session.id;
							return (
								<TableRow key={session.id || session.token}>
									<TableCell className="max-w-xs">
										<div className="flex items-center gap-2">
											<span
												className="truncate"
												title={session.userAgent || undefined}
											>
												{session.userAgent ||
													m.accountSettings_unknownDevice()}
											</span>
											{isCurrentSession && (
												<Badge
													variant="secondary"
													className="text-xs shrink-0"
												>
													{m.accountSettings_current()}
												</Badge>
											)}
										</div>
									</TableCell>
									<TableCell>
										{session.ipAddress || m.accountSettings_unknownLocation()}
									</TableCell>
									<TableCell>{formatDate(session.createdAt)}</TableCell>
									<TableCell>{formatDate(session.updatedAt)}</TableCell>
									<TableCell>
										<DropdownMenu>
											<DropdownMenuTrigger
												render={
													<Button variant="ghost" size="icon-sm">
														<MoreHorizontalIcon />
													</Button>
												}
											/>
											<DropdownMenuContent align="end">
												<DropdownMenuItem
													onClick={() => revokeSession(session.token)}
													className="text-destructive focus:text-destructive"
												>
													{m.accountSettings_revokeSession()}
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</CardContent>
		</Card>
	);
}
